import {Container, Graphics, Sprite} from "pixi.js";
import {getTexture} from "../assets.js";
import {Vector} from "../i.js"; // ../vector.js
import {logger} from "../logger.js";
import {Thing, THING_TYPES} from "../i.js";
import {Clicker} from "../i.js";
import {Tab} from "../i.js"; // ./tab.js

// Map is already a term for the data structure...
export class GPS extends Tab {
	// The height of the area displayed by the GPS
	static FLARES_VERTICAL = 500;

	// Create a new gps
	// Each unicenter should create exactly one gps. unicenter is a
	// reference to the unicenter.
	constructor(unicenter) {
		super(
			unicenter,
			null,
			Tab.LOCATIONS.RIGHT,
			"images/icons/gps.svg",
			220,
			"GPS"
		);

		// Array of ids of things to draw arrows to
		this.arrows = new Set();
		// A backup of arrows made when right before clearing all arrows
		// Used to undo a clear.
		this._last_arrows = new Set();

		let availableMask = new Graphics;
		this.display.availableMask = availableMask;
		this.display.addChild(availableMask);

		let areas = new Graphics();
		areas.mask = availableMask;
		this.display.areas = areas;
		this.display.addChild(areas);

		let lines = new Graphics();
		lines.mask = availableMask;
		this.display.lines = lines;
		this.display.addChild(lines);

		let icons = new Container();
		icons.mask = availableMask;
		icons.sortableChildren = true;
		this.display.icons = icons;
		this.display.addChild(icons);

		let border = new Graphics();
		this.display.border = border;
		this.display.addChild(border);
	}

	// Update the gps
	// Call this every tick. visible is a boolean that is true if the display is
	// visible this tick. size is a vector in pixels representing the desired
	// size of the display.
	update(visible, size) {
		if (visible) {
			for (let icon of this.display.icons.children) {
				if (icon.clicker.wasClicked()) {
					if (icon.isPlayer) {
						if (this.arrows.size === 0) {
							this.arrows = new Set(this._last_arrows);
						} else {
							this._last_arrows = new Set(this.arrows);
							this.arrows.clear();
						}
						break;
					}

					// Any other change means that undo is no longer possible
					this._last_arrows.clear();

					if (this.arrows.has(icon.id)) {
						this.arrows.delete(icon.id);
					} else {
						this.arrows.add(icon.id);
					}
				}
			}
		}

		this.arrows.forEach(id => {
			let thing = this.unicenter.game.space.requestThingById(id);
			if ((thing === null) || (thing.state !== Thing.STATES.ALIVE)) {
				this.arrows.delete(id);
			}
		});

		super.update(visible, size);

		for (let icon of this.display.icons.children) {
			icon.clicker.clearStuff();
		}
	}

	// Refresh the display, redrawing everything from scratch
	// This method only needs to be called when something actually changed, but
	// it's okay to call it every tick.
	_refreshDisplay() {
		let size = this.display.size;

		// GPS scale: pixels in the GPS display per flare in space
		let gpsPixelsPerFlare = size.y / GPS.FLARES_VERTICAL;
		// pos corresponding to the top-left of the GPS
		let posTopLeft = Vector.diff(
			this.unicenter.game.space.player.pos,
			Vector.scale(size, 1 / (2 * gpsPixelsPerFlare))
		);
		// A function to convert to GPS pixels
		function posToGpsPixels(pos) {
			return Vector.diff(pos, posTopLeft).scale(gpsPixelsPerFlare);
		}

		let availableMask = this.display.availableMask;
		availableMask
			.clear()
			.rect(0, 0, size.x, size.y)
			.fill(0xffffff);

		this._refreshAreas(size, posToGpsPixels, gpsPixelsPerFlare);
		this._refreshLines(size, posToGpsPixels);
		this._refreshIcons(size, posToGpsPixels);

		let border = this.display.border;
		border
			.clear()
			.rect(0, 0, size.x, size.y)
			.stroke(this.unicenter.requestStroke(this.colorHighlight, "NARROW"));
	}

	// Refresh areas
	// Args:
	//   size              - a vector in pixels representing the size of the display
	//                       (= the size of the gps for now)
	//   posToGpsPixels    - a function mapping a vector to a vector
	//   gpsPixelsPerFlare - number
	_refreshAreas(size, posToGpsPixels, gpsPixelsPerFlare) {
		let areas = this.display.areas;

		let areaInfo = this.unicenter.game.space.density.getAreaInfo(
			this.unicenter.game.space.time
		);

		areas.clear();
		for (let area of areaInfo) {
			switch (area[0]) {
				case "CIRCLE": {
					let color = area[1];
					let centerPos = area[2];
					let radiusFlares = area[3];

					let centerPixels = posToGpsPixels(centerPos);
					let radiusPixels = radiusFlares * gpsPixelsPerFlare;

					areas
						.circle(centerPixels.x, centerPixels.y, radiusPixels)
						.fill((color === "EMPTY") ? this.colorBase : color);
					break;
				}
				case "ARC": {
					let color = area[1];
					let centerPos = area[2];
					let radiusFlares = area[3];
					let startAngle = area[4];
					let endAngle = area[5];

					let centerPixels = posToGpsPixels(centerPos);
					let radiusPixels = radiusFlares * gpsPixelsPerFlare;
					areas
						// Not sure why this moveTo() is needed, but arcs don't render
						// correctly otherwise
						.moveTo(centerPixels.x, centerPixels.y)
						.arc(
							centerPixels.x, centerPixels.y,
							radiusPixels,
							startAngle, endAngle
						)
						.fill((color === "EMPTY") ? this.colorBase : color);
					break;
				}
				case "LINE": {
					let color = area[1];
					let widthFlares = area[2];
					let startPointFlares = area[3];
					let endPointFlares = area[4];

					let widthPixels = widthFlares * gpsPixelsPerFlare;
					let startPointPixels = posToGpsPixels(startPointFlares);
					let endPointPixels = posToGpsPixels(endPointFlares);
					areas
						.moveTo(startPointPixels.x, startPointPixels.y)
						.lineTo(endPointPixels.x, endPointPixels.y)
						.stroke({
							color: (color === "EMPTY") ? this.colorBase : color,
							width: widthPixels
						});
					break;
				}
				default:
					logger.error("gps.js", `unknown area shape: ${area[0]}`);
					break;
			}
		}
	}

	// Refresh lines corresponding to arrows
	// Args:
	//   size           - a vector in pixels representing the size of the display
	//                    (= the size of the gps for now)
	//   posToGpsPixels - a function mapping a vector to a vector
	_refreshLines(size, posToGpsPixels) {
		let lines = this.display.lines;

		let playerGpsPixels = posToGpsPixels(this.unicenter.game.space.player.pos);

		lines.clear();

		this.arrows.forEach(arrow => {
			let target = this.unicenter.game.space.requestThingById(arrow);
			if (target === null) return;
			let targetGpsPixels = posToGpsPixels(target.pos);
			lines
				.moveTo(playerGpsPixels.x, playerGpsPixels.y)
				.lineTo(targetGpsPixels.x, targetGpsPixels.y)
				.stroke({
					width: this.unicenter.requestStroke(0x000000, "NARROW").width,
					color: (target.TYPE === THING_TYPES.STATION) ? 0x00ff00 : 0xffffff,
					cap: "round"
				});
		});
	}

	// Refresh the thing icons
	// Args:
	//   size           - a vector in pixels representing the size of the display
	//                    (= the size of the gps for now)
	//   posToGpsPixels - a function mapping a vector to a vector
	_refreshIcons(size, posToGpsPixels) {
		let icons = this.display.icons;

		let gpsIconsData = this.unicenter.game.space.things
			.filter(
				thing => (thing.gpsIcon !== null)
			)
			.map(thing => ({
				isPlayer: thing === this.unicenter.game.space.player,
				id: thing.id,
				pos: thing.pos,
				rot: thing.rot,
				gpsIcon: thing.gpsIcon,
				rotateGpsIcon: thing.rotateGpsIcon,
				zIndex: thing.zIndex
			}));

		// Remove extra icons
		while (icons.children.length > gpsIconsData.length) {
			icons.removeChildAt(0);
		}
		// Add icons if necessary
		while (icons.children.length < gpsIconsData.length) {
			let iconToAdd = new Sprite();
			iconToAdd.anchor.set(0.5, 0.5);
			// TODO: Remove this clicker when icon is removed
			iconToAdd.clicker = new Clicker(iconToAdd);
			icons.addChild(iconToAdd);
		}

		for (let i = 0; i < gpsIconsData.length; ++i) {
			let icon = icons.children[i];
			let iconPos = posToGpsPixels(gpsIconsData[i].pos);
			// TODO: Hide icons that are way outside the window
			icon.texture = getTexture(gpsIconsData[i].gpsIcon);
			icon.position.copyFrom(iconPos);
			icon.rotation = gpsIconsData[i].rotateGpsIcon
				? gpsIconsData[i].rot + Math.PI / 2
				: 0;
			icon.scale.set((icon.clicker.isHovering() ? 0.0011 : 0.001) * size.x);
			icon.zIndex = gpsIconsData[i].zIndex;

			icon.id = gpsIconsData[i].id;
			icon.isPlayer = gpsIconsData[i].isPlayer;
		}
	}
}
